import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Loader2, UserPlus, Landmark, Layers, CreditCard, ArrowRightLeft, Check, Scissors } from "lucide-react";

const inputCls = "w-full rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[11px] font-normal text-slate-800";

/** Gelber Prüfpunkt «Lieferant»: neuen Lieferanten mit Beleg-IBAN per Klick anlegen. */
export function CreateSupplierAction({ companyId, item, onUpdated }) {
  const ex = item.extracted || {};
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: ex.supplier_name || "",
    iban: ex.iban || "",
    vat_id: ex.supplier_vat_id || "",
    address: ex.supplier_address || "",
    postal_code: "",
    city: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/create-supplier`, form);
      const c = r.data.contact || {};
      const hr = c.zefix_status ? ` · HR: ${c.zefix_status}${c.vat_id ? ` (${c.vat_id})` : ""}` : "";
      toast.success(`${r.data.created ? "Lieferant angelegt" : "Bestehender Lieferant verknüpft"}: ${c.name}${hr}`);
      onUpdated?.({ ...item, checks: r.data.checks, extracted: r.data.extracted, routing: r.data.routing || item.routing });
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Lieferant konnte nicht angelegt werden"); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <span className="ml-1 inline-flex flex-wrap items-center gap-1" data-testid={`inbox-create-supplier-quick-${item.id}`}>
        <input
          className="w-56 rounded border border-amber-400 bg-white px-1.5 py-px text-[11px] font-normal text-slate-800"
          value={form.name}
          onChange={set("name")}
          onKeyDown={(e) => { if (e.key === "Enter" && form.name.trim()) submit(); }}
          placeholder="Lieferantenname prüfen …"
          title="Gelesenen Namen korrigieren und direkt übernehmen"
          data-testid={`inbox-create-supplier-name-${item.id}`}
        />
        <button type="button" onClick={submit} disabled={busy || !form.name.trim()} className="inline-flex items-center gap-1 rounded bg-amber-600 px-1.5 py-px text-[10px] font-medium text-white hover:bg-amber-700 disabled:opacity-50" data-testid={`inbox-create-supplier-${item.id}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />} Als Lieferant übernehmen
        </button>
        <button type="button" onClick={() => setOpen(true)} className="text-[10px] underline opacity-70" data-testid={`inbox-create-supplier-more-${item.id}`}>mehr Felder</button>
      </span>
    );
  }
  return (
    <div className="mt-1 w-full rounded border border-amber-300 bg-white/80 p-2 space-y-1" data-testid={`inbox-create-supplier-form-${item.id}`}>
      <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Name *" data-testid="create-supplier-name" />
      <div className="grid grid-cols-2 gap-1">
        <input className={inputCls} value={form.iban} onChange={set("iban")} placeholder="IBAN (vom Beleg)" data-testid="create-supplier-iban" />
        <input className={inputCls} value={form.vat_id} onChange={set("vat_id")} placeholder="UID (CHE-…)" data-testid="create-supplier-vat" />
      </div>
      <div className="grid grid-cols-[2fr_1fr_1fr] gap-1">
        <input className={inputCls} value={form.address} onChange={set("address")} placeholder="Strasse" data-testid="create-supplier-address" />
        <input className={inputCls} value={form.postal_code} onChange={set("postal_code")} placeholder="PLZ" />
        <input className={inputCls} value={form.city} onChange={set("city")} placeholder="Ort" />
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={submit} disabled={busy || !form.name.trim()} className="inline-flex items-center gap-1 rounded bg-amber-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-amber-700 disabled:opacity-50" data-testid="create-supplier-submit">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />} Lieferant anlegen
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-[11px] underline opacity-70" data-testid="create-supplier-cancel">Abbrechen</button>
        <span className="text-[10px] opacity-70">Fehlende UID/Adresse werden aus dem Handelsregister ergänzt.</span>
      </div>
    </div>
  );
}

/** Gelber Prüfpunkt «IBAN-Abgleich»: Beleg-IBAN beim Kontakt hinterlegen. */
export function AdoptIbanAction({ companyId, item, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/adopt-iban`);
      toast.success(`IBAN ${r.data.iban} beim Kontakt hinterlegt`);
      onUpdated?.({ ...item, checks: r.data.checks, extracted: r.data.extracted, routing: r.data.routing || item.routing });
    } catch (e) { toast.error(e.response?.data?.detail || "IBAN konnte nicht übernommen werden"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy} className="ml-1 inline-flex items-center gap-1 rounded border border-amber-400 bg-white px-1.5 py-px text-[10px] font-medium hover:bg-amber-100" data-testid={`inbox-adopt-iban-${item.id}`}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Landmark className="h-3 w-3" />} IBAN übernehmen
    </button>
  );
}

/** Gelber Prüfpunkt «Zusammengehörig»: zwei Einträge (z.B. Seite 1/2 + 2/2) zu einem Beleg zusammenführen. */
export function MergeRelatedAction({ companyId, item, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const other = (item.related_items || [])[0];
  if (!other) return null;
  const run = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/merge-related`, { other_id: other.id });
      toast.success(`Zusammengeführt: 1 Hauptdokument + ${r.data.attachments_count} Beilage(n)`);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Zusammenführen fehlgeschlagen"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy} className="ml-1 inline-flex items-center gap-1 rounded border border-amber-400 bg-white px-1.5 py-px text-[10px] font-medium hover:bg-amber-100" data-testid={`inbox-merge-related-${item.id}`}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Layers className="h-3 w-3" />} Zusammenführen
    </button>
  );
}

/** Gelber Prüfpunkt «Zahlungsmittel»: unbekannte Karte einmal zuordnen – Firmenkarte oder Privatkarte eines Mitarbeiters (→ Spesen). */
export function AssignCardAction({ companyId, item, onUpdated, onRefresh }) {
  const pc = item.extracted?.payment_card || {};
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("company");
  const [employeeId, setEmployeeId] = useState("");
  const [label, setLabel] = useState("");
  const [employees, setEmployees] = useState([]);
  const [cards, setCards] = useState([]);
  const [aliasOf, setAliasOf] = useState("");
  const [wallet, setWallet] = useState("apple_pay");
  const [companies, setCompanies] = useState([]);
  const [targetCompanyId, setTargetCompanyId] = useState("");

  const openForm = async () => {
    setOpen(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/employees`);
      setEmployees((r.data || []).filter((e) => !e.archived_at));
    } catch { setEmployees([]); }
    try {
      const r = await apiClient.get(`/my/companies`);
      setCompanies((r.data || []).filter((c) => c._id !== companyId && !c.archived));
    } catch { setCompanies([]); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (kind === "other_company") {
        const target = companies.find((c) => c._id === targetCompanyId);
        await apiClient.post(`/companies/${targetCompanyId}/cards`, { last4: pc.last4, brand: pc.brand || null, kind: "company", label: label || `Firmenkarte …${pc.last4}` });
        await apiClient.post(`/companies/${companyId}/inbox/${item.id}/move-company`, { target_company_id: targetCompanyId });
        toast.success(`Firmenkarte …${pc.last4} bei «${target?.name || "andere Firma"}» gespeichert – Beleg dorthin übertragen`);
        setOpen(false);
        onRefresh?.();
        return;
      }
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/assign-card`, { kind, employee_id: employeeId || null, label: label || null, alias_of_last4: kind === "wallet_alias" ? aliasOf : null, wallet: kind === "wallet_alias" ? wallet : null });
      const n = r.data.applied_to_pending || 0;
      toast.success((kind === "wallet_alias" ? `…${pc.last4} als ${wallet === "google_pay" ? "Google Pay" : wallet === "samsung_pay" ? "Samsung Pay" : "Apple Pay"}-Nummer der Karte …${aliasOf} gespeichert` : kind === "company" ? `Firmenkarte …${pc.last4} gespeichert` : `Privatkarte …${pc.last4} gespeichert – Beleg als Spesen markiert`) + (n ? ` · auch auf ${n} weitere offene Belege angewendet` : ""));
      onUpdated?.({ ...item, checks: r.data.checks, extracted: r.data.extracted, routing: r.data.routing || item.routing, source_meta: r.data.source_meta || item.source_meta });
      setOpen(false);
      if (n) onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Karte konnte nicht zugeordnet werden"); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={openForm} className="ml-1 inline-flex items-center gap-1 rounded border border-amber-400 bg-white px-1.5 py-px text-[10px] font-medium hover:bg-amber-100" data-testid={`inbox-assign-card-${item.id}`}>
        <CreditCard className="h-3 w-3" /> Karte zuordnen
      </button>
    );
  }
  return (
    <div className="mt-1 w-full rounded border border-amber-300 bg-white/80 p-2 space-y-1" data-testid={`inbox-assign-card-form-${item.id}`}>
      <div className="flex flex-wrap items-center gap-3 text-[11px]">
        <span className="font-medium">{pc.brand || "Karte"} …{pc.last4}</span>
        <label className="inline-flex items-center gap-1"><input type="radio" checked={kind === "company"} onChange={() => setKind("company")} data-testid="assign-card-kind-company" /> Firmenkarte</label>
        <label className="inline-flex items-center gap-1"><input type="radio" checked={kind === "employee_private"} onChange={() => setKind("employee_private")} data-testid="assign-card-kind-private" /> Privatkarte eines Mitarbeiters (Spesen)</label>
        <label className="inline-flex items-center gap-1" title="Mit Apple/Google Pay bezahlt? Dann steht auf dem Bon die Gerätenummer, nicht die echte Kartennummer"><input type="radio" checked={kind === "wallet_alias"} onChange={() => { setKind("wallet_alias"); if (!cards.length) apiClient.get(`/companies/${companyId}/cards`).then((r) => setCards((r.data?.items || []).filter((c) => !c.alias_of_last4))).catch(() => {}); }} data-testid="assign-card-kind-wallet" /> Apple/Google-Pay-Nummer einer bekannten Karte</label>
        {companies.length > 0 && (
          <label className="inline-flex items-center gap-1"><input type="radio" checked={kind === "other_company"} onChange={() => setKind("other_company")} data-testid="assign-card-kind-other" /> Karte einer anderen eigenen Firma</label>
        )}
      </div>
      {kind === "company" && (
        <input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Bezeichnung / Inhaber (z.B. Firmenkarte Max)" data-testid="assign-card-label" />
      )}
      {kind === "wallet_alias" && (
        <div className="flex flex-wrap gap-1">
          <select className={inputCls} value={aliasOf} onChange={(e) => setAliasOf(e.target.value)} data-testid="assign-card-alias-of">
            <option value="">Echte Karte wählen …</option>
            {cards.map((c) => <option key={c.id} value={c.last4}>…{c.last4} {c.brand || ""} · {c.kind === "company" ? "Firmenkarte" : "Privatkarte"} {c.holder_name || c.label || ""}</option>)}
          </select>
          <select className={inputCls} value={wallet} onChange={(e) => setWallet(e.target.value)} data-testid="assign-card-wallet">
            <option value="apple_pay">Apple Pay</option><option value="google_pay">Google Pay</option><option value="samsung_pay">Samsung Pay</option>
          </select>
        </div>
      )}
      {kind === "employee_private" && (
        <select className={inputCls} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} data-testid="assign-card-employee">
          <option value="">Mitarbeiter wählen …</option>
          {employees.map((e) => <option key={e._id || e.id} value={e._id || e.id}>{e.first_name} {e.last_name}</option>)}
        </select>
      )}
      {kind === "other_company" && (
        <div className="flex flex-wrap gap-1">
          <select className={inputCls} value={targetCompanyId} onChange={(e) => setTargetCompanyId(e.target.value)} data-testid="assign-card-company">
            <option value="">Firma wählen …</option>
            {companies.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Bezeichnung / Inhaber (optional)" data-testid="assign-card-other-label" />
        </div>
      )}
      <div className="flex items-center gap-2">
        <button type="button" onClick={submit} disabled={busy || (kind === "employee_private" && !employeeId) || (kind === "other_company" && !targetCompanyId) || (kind === "wallet_alias" && !aliasOf)} className="inline-flex items-center gap-1 rounded bg-amber-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-amber-700 disabled:opacity-50" data-testid="assign-card-submit">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />} {kind === "other_company" ? "Speichern & Beleg übertragen" : "Speichern"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-[11px] underline opacity-70">Abbrechen</button>
        <span className="text-[10px] opacity-70">{kind === "other_company" ? "Die Karte wird bei der Zielfirma als Firmenkarte gespeichert; der Beleg wandert in deren Posteingang." : "Das System merkt sich die Karte für künftige Belege."}</span>
      </div>
    </div>
  );
}

/** Roter Prüfpunkt: Karte gehört einer anderen eigenen Firma → Beleg intern dorthin übertragen. */
export function MoveCompanyAction({ companyId, item, onRefresh }) {
  const pc = item.extracted?.payment_card || {};
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!pc.other_company_id) return;
    if (!window.confirm(`Beleg an «${pc.other_company_name}» übertragen? Er verschwindet aus diesem Posteingang.`)) return;
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/move-company`, { target_company_id: pc.other_company_id });
      toast.success(`Beleg an ${r.data.target_company_name || "andere Firma"} übertragen`);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Übertragung fehlgeschlagen"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy} className="ml-1 inline-flex items-center gap-1 rounded border border-rose-400 bg-white px-1.5 py-px text-[10px] font-medium hover:bg-rose-100" data-testid={`inbox-move-company-${item.id}`}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightLeft className="h-3 w-3" />} An {pc.other_company_name || "andere Firma"} übertragen
    </button>
  );
}

/** Gelber Prüfpunkt «Automatisch zusammengeführt»: bestätigen oder Beilage wieder trennen. */
export function ConfirmMergeAction({ companyId, item, onUpdated, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/confirm-merge`);
      toast.success("Zusammenführung bestätigt");
      onUpdated?.({ ...item, checks: r.data.checks, routing: r.data.routing || item.routing });
    } catch (e) { toast.error(e.response?.data?.detail || "Bestätigung fehlgeschlagen"); } finally { setBusy(false); }
  };
  const detach = async () => {
    const files = item.auto_merged?.files || [];
    const atts = (item.attachments || []).filter((a) => files.includes(a.file_name));
    const target = atts[0] || (item.attachments || [])[item.attachments.length - 1];
    if (!target) { toast.error("Keine Beilage zum Trennen gefunden"); return; }
    if (!window.confirm(`«${target.file_name}» wieder als eigenes Dokument in den Posteingang stellen?`)) return;
    setBusy(true);
    try {
      await apiClient.post(`/companies/${companyId}/inbox/${item.id}/attachments/${target.file_id}/detach`);
      toast.success("Beilage getrennt – wieder als eigenes Dokument im Posteingang");
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Trennen fehlgeschlagen"); } finally { setBusy(false); }
  };
  return (
    <span className="ml-1 inline-flex items-center gap-1">
      <button type="button" onClick={confirm} disabled={busy} className="inline-flex items-center gap-1 rounded border border-emerald-400 bg-white px-1.5 py-px text-[10px] font-medium text-emerald-800 hover:bg-emerald-50" data-testid={`inbox-confirm-merge-${item.id}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Passt, gehört zusammen
      </button>
      <button type="button" onClick={detach} disabled={busy} className="inline-flex items-center gap-1 rounded border border-rose-300 bg-white px-1.5 py-px text-[10px] font-medium text-rose-700 hover:bg-rose-50" data-testid={`inbox-detach-merge-${item.id}`}>
        <Scissors className="h-3 w-3" /> Trennen
      </button>
    </span>
  );
}


/** Prüfpunkt «Bezugsteuer»: Dienstleistung aus dem Ausland → Bezugsteuer 8.1 % buchen (ja/nein). Lieferanten-Regel wird beim Bestätigen gelernt. */
export function ReverseChargeAction({ companyId, item, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const current = item.extracted?.reverse_charge;
  const decide = async (enabled) => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/reverse-charge`, { enabled });
      toast.success(enabled === null ? "Entscheid zurückgesetzt" : enabled ? "Bezugsteuer 8.1 % wird beim Buchen erfasst" : "Keine Bezugsteuer für diesen Beleg");
      onUpdated?.({ ...item, checks: r.data.checks, extracted: r.data.extracted, routing: r.data.routing || item.routing });
    } catch (e) { toast.error(e.response?.data?.detail || "Entscheid konnte nicht gespeichert werden"); } finally { setBusy(false); }
  };
  const cls = (active) => `ml-1 inline-flex items-center gap-1 rounded border px-1.5 py-px text-[10px] font-medium ${active ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-slate-300 bg-white hover:bg-slate-50"}`;
  return (
    <span className="inline-flex items-center" data-testid={`inbox-reverse-charge-${item.id}`}>
      <button type="button" disabled={busy} onClick={() => { if (current !== true) decide(true); }} className={cls(current === true)} title={current === true ? "Bezugsteuer ist gewählt" : "Bezugsteuer 8.1 % beim Buchen erfassen"} data-testid={`inbox-reverse-charge-yes-${item.id}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : current === true ? <Check className="h-3 w-3" /> : null} Bezugsteuer ja
      </button>
      <button type="button" disabled={busy} onClick={() => { if (current !== false) decide(false); }} className={cls(current === false)} title={current === false ? "Keine Bezugsteuer gewählt" : "Keine Bezugsteuer für diesen Beleg"} data-testid={`inbox-reverse-charge-no-${item.id}`}>
        {current === false ? <Check className="h-3 w-3" /> : null} nein
      </button>
      {current != null && (
        <button type="button" disabled={busy} onClick={() => decide(null)} className="ml-1 text-[10px] text-slate-400 underline hover:text-slate-700" title="Entscheid zurücksetzen" data-testid={`inbox-reverse-charge-reset-${item.id}`}>zurücksetzen</button>
      )}
    </span>
  );
}
